import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Input, Select, Card, Tabs, Tag } from 'antd';
import { SearchOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ClaimStatusTag from '../components/ClaimStatusTag';
import ClaimDetailDrawer from '../components/ClaimDetailDrawer';
import ApprovalActionModal from '../components/ApprovalActionModal';
import {
  getAllClaims,
  approverDecision,
  financeDecision,
  getClaimDetail,
} from '../costsClaimAction';
import { getUser } from 'utils/auth';

const { Option } = Select;
const { TabPane } = Tabs;

export const ClaimApprovalList: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = getUser();
  const { allClaims, loading, total, currentClaim } = useSelector(
    (state: any) => state.costsClaim || {},
  );

  const [activeTab, setActiveTab] = useState<'L1' | 'L2'>('L1');
  const [searchKw, setSearchKw] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [targetClaim, setTargetClaim] = useState<any>(null);
  const [defaultDecision, setDefaultDecision] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [approvalLevel, setApprovalLevel] = useState<'L1' | 'L2'>('L1');

  useEffect(() => {
    loadData();
  }, [dispatch, activeTab]);

  const loadData = (kw = searchKw) => {
    const params: any = { page: 1, perPage: 50 };
    if (activeTab === 'L1') {
      params.status = 'SUBMITTED';
    } else {
      params.status = 'APPROVED_BY_APPROVER';
    }
    if (kw) params.keyword = kw;
    dispatch(getAllClaims(params) as any);
  };

  const handleOpenApprovalModal = (
    claim: any,
    level: 'L1' | 'L2',
    action: 'APPROVE' | 'REJECT',
  ) => {
    setTargetClaim(claim);
    setApprovalLevel(level);
    setDefaultDecision(action);
    setApprovalModalVisible(true);
  };

  const handleConfirmDecision = async (action: 'APPROVE' | 'REJECT', comment: string) => {
    if (!targetClaim) return;
    if (approvalLevel === 'L1') {
      await dispatch(approverDecision({ id: targetClaim.id, body: { action, comment } }) as any);
    } else {
      await dispatch(financeDecision({ id: targetClaim.id, body: { action, comment } }) as any);
    }
    setApprovalModalVisible(false);
    setDrawerVisible(false);
    loadData();
  };

  const handleViewDetail = (record: any) => {
    dispatch(getClaimDetail(record.id) as any);
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: 'Mã Claim',
      dataIndex: 'claimCode',
      key: 'claimCode',
      render: (text: string, record: any) => (
        <Button type="link" onClick={() => handleViewDetail(record)} style={{ padding: 0 }}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Người tạo',
      dataIndex: 'creatorName',
      key: 'creatorName',
      render: (val: string, record: any) => (
        <span style={{ fontWeight: '500' }}>{val || record.createdBy || '-'}</span>
      ),
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number, record: any) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {val ? `${Number(val).toLocaleString('vi-VN')} ${record.currency || 'VND'}` : '0 VND'}
        </span>
      ),
    },
    {
      title: 'Ngày nộp',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      render: (d: string) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (st: string) => <ClaimStatusTag status={st} />,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 90,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          Xem
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card title="Danh sách Yêu cầu Claim Cần Phê duyệt">
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as any)}
          style={{ marginBottom: 16 }}
        >
          <TabPane tab="Chờ Duyệt Cấp 1 (Approver)" key="L1" />
          <TabPane tab="Chờ Duyệt Cấp 2 (Finance)" key="L2" />
        </Tabs>

        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Tìm theo mã / tiêu đề..."
            prefix={<SearchOutlined />}
            allowClear
            value={searchKw}
            onChange={(e) => {
              const val = e.target.value;
              setSearchKw(val);
              if (!val) {
                loadData('');
              }
            }}
            onPressEnter={() => loadData(searchKw)}
            style={{ width: 250 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => loadData(searchKw)}>
            Tìm kiếm
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={allClaims}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <ClaimDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        claim={currentClaim}
        canApproveL1={
          activeTab === 'L1' &&
          (currentClaim?.createdBy !== currentUser?.id || currentUser?.isSystemAdmin)
        }
        canApproveL2={
          activeTab === 'L2' &&
          (currentClaim?.createdBy !== currentUser?.id || currentUser?.isSystemAdmin)
        }
        onApproveL1={(c) => handleOpenApprovalModal(c, 'L1', 'APPROVE')}
        onRejectL1={(c) => handleOpenApprovalModal(c, 'L1', 'REJECT')}
        onApproveL2={(c) => handleOpenApprovalModal(c, 'L2', 'APPROVE')}
        onRejectL2={(c) => handleOpenApprovalModal(c, 'L2', 'REJECT')}
      />

      <ApprovalActionModal
        visible={approvalModalVisible}
        onCancel={() => setApprovalModalVisible(false)}
        onSubmit={handleConfirmDecision}
        defaultAction={defaultDecision}
        title={`Xác nhận quyết định duyệt ${approvalLevel === 'L1' ? 'Cấp 1' : 'Finance'}`}
        loading={loading}
      />
    </div>
  );
};

export default ClaimApprovalList;
